/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React, { Dispatch, FC, SetStateAction, useCallback, useState } from 'react'
import { produce } from 'immer'
import { Label } from '@progress/kendo-react-labels'
import {
  NumericTextBox,
  Switch,
  TextArea,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs'
import {
  StackLayout,
  GridLayout,
  GridLayoutItem,
  CardActions
} from '@progress/kendo-react-layout'
import { Button } from '@progress/kendo-react-buttons'

import { Contents } from '../../../components/layouts'
import { ExpenseInformationTypeB } from './ExpenseInformationTypeB'
import { ExpenseInformationTypeA } from './ExpenseInformationTypeA'
import { ExpenseInformationTypeC } from './ExpenseInformationTypeC'
import { ExpenseInformationTypeD } from './ExpenseInformationTypeD'
import { ExpenseInformationTypeE } from './ExpenseInformationTypeE'
import { ExpenseInformationAppropriateTypeA } from './ExpenseInformationAppropriateTypeA'
import { ExpenseInformationAppropriateTypeB } from './ExpenseInformationAppropriateTypeB'
import { ExpenseInformationAppropriateTypeC } from './ExpenseInformationAppropriateTypeC'
import { ExpenseInformationAppropriateTypeD } from './ExpenseInformationAppropriateTypeD'
import { ExpenseInformationAppropriateTypeE } from './ExpenseInformationAppropriateTypeE'
import { Marker } from '../../../components/elements'
import { ConfirmDialog } from '../../../components/parts'
import { SimulationCalculationInfoResItem } from '@gateway.Simulation'
import { useAppSelector } from '../../../stores'
import { BlobFileService } from '../../../services'

interface ExpenseCardProps {
  type: string | null
  workYamadumi: SimulationCalculationInfoResItem
  isReadonlyDisabled: boolean
  onChange: (id: string, value: number | null) => void
}

const ExpenseCard: FC<ExpenseCardProps> = React.memo(({
  type,
  workYamadumi,
  isReadonlyDisabled,
  onChange
}) => {
  switch (type) {
    case 'A':
      return (
        workYamadumi.yamadumiAutoKbn === '2'
          ? <ExpenseInformationAppropriateTypeA
              workYamadumi={workYamadumi}
              isReadonlyDisabled={true}
              onChange={onChange}
            />
          : <ExpenseInformationTypeA
            workYamadumi={workYamadumi}
            isReadonlyDisabled={isReadonlyDisabled}
            onChange={onChange}
          />
      )
    case 'B':
      return (
        workYamadumi.yamadumiAutoKbn === '2'
          ? <ExpenseInformationAppropriateTypeB
              workYamadumi={workYamadumi}
              isReadonlyDisabled={true}
              onChange={onChange}
            />
          : <ExpenseInformationTypeB
            workYamadumi={workYamadumi}
            isReadonlyDisabled={isReadonlyDisabled}
            onChange={onChange}
          />
      )
    case 'C':
      return (
        workYamadumi.yamadumiAutoKbn === '2'
          ? <ExpenseInformationAppropriateTypeC
              workYamadumi={workYamadumi}
              isReadonlyDisabled={true}
              onChange={onChange}
            />
          : <ExpenseInformationTypeC
            workYamadumi={workYamadumi}
            isReadonlyDisabled={isReadonlyDisabled}
            onChange={onChange}
          />
      )
    case 'D':
      return (
        workYamadumi.yamadumiAutoKbn === '2'
          ? <ExpenseInformationAppropriateTypeD
              workYamadumi={workYamadumi}
              isReadonlyDisabled={true}
              onChange={onChange}
            />
          : <ExpenseInformationTypeD
            workYamadumi={workYamadumi}
            isReadonlyDisabled={isReadonlyDisabled}
            onChange={onChange}
          />
      )
    case 'E':
      return (
        workYamadumi.yamadumiAutoKbn === '2'
          ? <ExpenseInformationAppropriateTypeE
              workYamadumi={workYamadumi}
              isReadonlyDisabled={true}
              onChange={onChange}
            />
          : <ExpenseInformationTypeE
            workYamadumi={workYamadumi}
            isReadonlyDisabled={isReadonlyDisabled}
            onChange={onChange}
          />
      )
    default:
      return (
        <ExpenseInformationTypeA
          workYamadumi={workYamadumi}
          isReadonlyDisabled={isReadonlyDisabled}
          onChange={onChange}
        />
      )
  }
})

interface ManualSimulationAllotmentProps {
  workYamadumi: SimulationCalculationInfoResItem
  setWorkYamadumi: Dispatch<
  SetStateAction<SimulationCalculationInfoResItem | undefined>
  >
  switchValChangeStates: (value: boolean) => void
  isReadonlyDisabled: boolean
  isToggleDisabled: boolean
  onChange: (id: string, value: number | null) => void
  handleUpdataData: (type: string, id: string, title: string, args: string[]) => void
}

export const ExpenseInformation: FC<ManualSimulationAllotmentProps> = React.memo(({
  workYamadumi,
  setWorkYamadumi,
  switchValChangeStates,
  isReadonlyDisabled,
  isToggleDisabled,
  onChange,
  handleUpdataData
}) => {
  const initialData = useAppSelector((x) => x.common.initialData)
  // 適正工期ステータスリスト
  const tekiseiKokiStatuses = initialData.codeNames('TE02')
  // 費目
  const expenses = initialData.codeNames('HI01')

  // 費目要否 Dialog
  const [confirmDialogIsNeedExpensesShow, setConfirmDialogIsNeedExpensesShow] = useState(false)

  /**
   * 費目要否
   */
  const handleSwitchChange = () => {
    if (workYamadumi.expenseNotApplicableFlg === 1) {
      // ON→OFF
      setConfirmDialogIsNeedExpensesShow(true)
    } else {
      // OFF→ON
      setWorkYamadumi(
        produce((draft) => {
          if (draft === undefined) return
          draft.expenseNotApplicableFlg = 1
        })
      )
      switchValChangeStates(false)
    }
  }
  /**
   * 費目要否 dialogConfirm
   */
  const dialogConfirmIsNeedExpenses = () => {
    setConfirmDialogIsNeedExpensesShow(false)
    setWorkYamadumi(
      produce((draft) => {
        if (draft === undefined) return
        draft.expenseNotApplicableFlg = 0
      })
    )
    switchValChangeStates(true)
  }
  /**
   * 備考
   */
  const handleRemarkChange = useCallback((e: TextAreaChangeEvent) => {
    setWorkYamadumi(
      produce((draft) => {
        if (draft === undefined) return
        draft.remark = e.value
      })
    )
  }, [])
  /**
   * 優先度切替ボタン活性制御
   */
  const isDisabledPriorityButton = (expenseCd: string | null) => {
    if (expenseCd === null) {
      return true
    }
    const num8 = expenses.find(ex => ex.CODE === expenseCd)?.NUM8
    return num8 !== 1
  }

  /** 添付資料ダウンロードボタン */
  const handleDownload = (fileName: string) => {
    new BlobFileService()
      .download(
        'tekiseikoki-output-done',
        fileName
      )
      .then((res) => {
        BlobFileService.downloadRedirectTo(res)
      })
      .finally(() => {})
  }

  return (
    <>
      <Contents>
        <StackLayout gap={3} orientation="vertical">
          <GridLayout style={{ width: '100%' }}>
            <GridLayoutItem row={1} col={1} style={{ marginRight: '10px' }}>
              <span style={{ fontSize: '2.0em' }}>
                <Marker themeColor="secondary" blocked>
                  {workYamadumi?.expenseNm}
                </Marker>
              </span>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={2}>
              <Label>費目要否</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={3}>
              <Switch
                disabled={isToggleDisabled}
                size={'small'}
                onChange={handleSwitchChange}
                checked={workYamadumi?.expenseNotApplicableFlg === 1}
              />
            </GridLayoutItem>
            <GridLayoutItem row={1} col={4}>
              <Label>費目工事金額</Label>
              <label style={{ color: 'gray', fontSize: 'small' }}>
                  {workYamadumi.unit8}
              </label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={5} style={{ marginRight: '10px' }}>
              <NumericTextBox
                disabled={isReadonlyDisabled}
                spinners={false}
                format={{ maximumFractionDigits: 0 }}
                value={workYamadumi?.expenseCost ?? undefined}
                onChange={(event) => onChange('expenseCost', event.value)}
              />
            </GridLayoutItem>
            <GridLayoutItem row={1} col={6}>
              <Label>備考</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={7}>
              <TextArea
                disabled={isReadonlyDisabled}
                value={workYamadumi?.remark ?? undefined}
                onChange={handleRemarkChange}
              />
            </GridLayoutItem>
            <GridLayoutItem row={1} col={8} style={{ marginLeft: '100px' }}>
              <span>前回更新日：</span>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={9}>
              <span>
                {workYamadumi?.decisionUpdYmd
                  ?.toString()
                  .replace(/^(\d{4})(\d{2})(\d{2})$/, '$1/$2')}
              </span>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={1} colSpan={2} style={{ marginRight: '10px' }}>
              <span style={{ fontSize: '1.5em' }}>
                <Marker themeColor="accent" blocked>
                  適正工期：
                  {tekiseiKokiStatuses.find(t => t.CODE === workYamadumi.status)?.NAME}
                </Marker>
              </span>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={8} colSpan={2}>
              <CardActions layout="end" style={{ marginRight: '-10px' }}>
                <Button
                  onClick={() => {
                    const msg = workYamadumi.priorityKbn === '2' ? '自動計算優先に切り替え' : '適正工期優先に切り替え'
                    handleUpdataData('5', 'I007', msg, [msg])
                  }}
                  className={'pcy-button-long'}
                  themeColor="primary"
                  disabled={workYamadumi.status === '1' || isDisabledPriorityButton(workYamadumi.expense)}
                >
                  {workYamadumi.priorityKbn === '2' ? '自動計算優先に切り替え' : '適正工期優先に切り替え'}
                </Button>
                <Button
                  onClick={() => {
                    const fileName = workYamadumi.status === '4' ? workYamadumi.errorFilePath1 : workYamadumi.resultFilePath1
                    handleDownload(fileName ?? '')
                  }}
                  className={'pcy-button-long'}
                  disabled={
                    !(
                      (workYamadumi.status === '2' && workYamadumi.resultFilePath1 !== null) ||
                      (workYamadumi.status === '4' && workYamadumi.errorFilePath1 !== null)
                    )
                  }
                >
                  適正工期結果
                </Button>
                <Button
                  onClick={() => handleDownload(workYamadumi.resultFilePath2 ?? '')}
                  className={'pcy-button-long'}
                  disabled={!(workYamadumi.status === '2' && workYamadumi.resultFilePath2 !== null)}
                >
                  工程表
                </Button>
              </CardActions>
            </GridLayoutItem>
          </GridLayout>
          <ExpenseCard
            type={workYamadumi?.autoCalculationMethod}
            workYamadumi={workYamadumi}
            isReadonlyDisabled={isReadonlyDisabled}
            onChange={onChange}
          />
        </StackLayout>
      </Contents>
      <ConfirmDialog
        title="費目削除確認ダイアログ"
        open={confirmDialogIsNeedExpensesShow}
        renderCloseButton
        id={'I011'}
        args={[workYamadumi?.expenseNm ?? '']}
        onClickOk={dialogConfirmIsNeedExpenses}
        onClickCancel={() => setConfirmDialogIsNeedExpensesShow(false)}
      />
    </>
  )
})
